
import Vue from 'vue'
import { getAngle, getPositionInCircle, inCircle } from '@/helpers/circle'
import JoystickPositionHandleSVG from '@/components/svgs/JoystickPositionHandleSVG.vue'
import JoystickRotationHandleSVG from '@/components/svgs/JoystickRotationHandleSVG.vue'
import JoystickRotationBackgroundSVG from '@/components/svgs/JoystickRotationBackgroundSVG.vue'
import JoystickScalingBackgroundSVG from '@/components/svgs/JoystickScalingBackgroundSVG.vue'
import JoystickPositionBackgroundSVG from '@/components/svgs/JoystickPositionBackgroundSVG.vue'
import CrossButton from '@/components/IconButtons/CrossButton.vue'
import AcceptButton from '@/components/IconButtons/AcceptButton.vue'
import ResetButton from '@/components/IconButtons/ResetButton.vue'
import { mapper } from '@/stores/store'
import mapRange from '@/helpers/map-range'
import { GAME_HEIGHT, GAME_WIDTH, MAX_SCALE, MIN_SCALE, DEFAULT_SCALE } from '@/constants'

let RAFHandle: number | undefined
let rafRotation = 0
let rafScale = 1
let rafPosition = { x: 0, y: 0 }

export default Vue.extend({
  components: {
    JoystickPositionHandleSVG,
    JoystickRotationHandleSVG,
    JoystickScalingBackgroundSVG,
    JoystickRotationBackgroundSVG,
    JoystickPositionBackgroundSVG,
    CrossButton,
    AcceptButton,
    ResetButton,
  },
  props: {
    isRotation: Boolean,
    isScaling: Boolean,
  },
  data() {
    return {
      DEFAULT_SCALE,
      joystickBox: {} as DOMRect,
      joystickCenter: { x: 0, y: 0 },
      pointerDown: false,
      initialRotation: 0,
      initialScale: DEFAULT_SCALE,
      initialPosition: { x: 0, y: 0 },
      handleHalfWidth: 38,
    }
  },
  computed: {
    ...mapper(['isTablet']),
    ...mapper('pin', ['position', 'rotation', 'scale', 'activeFeature']),
    joystickWrapper(): HTMLElement {
      return this.$refs.joystickWrapper as HTMLElement
    },
    joystick(): HTMLElement {
      return this.$refs.joystick as HTMLElement
    },
    handle(): HTMLElement {
      return this.$refs.handle as HTMLElement
    },
    closeButton(): HTMLElement {
      return this.$refs.closeButton as HTMLElement
    },
    acceptButton(): HTMLElement {
      return this.$refs.acceptButton as HTMLElement
    },
    resetButton(): HTMLElement {
      return this.$refs.resetButton as HTMLElement
    },
    isMoving(): boolean {
      return !this.isRotation && !this.isScaling
    },
    cssVars(): Record<string, string> {
      let handlePosition = { x: 0, y: 0 }

      if (this.isRotation) {
        handlePosition = getPositionInCircle(
          this.rotation,
          this.joystickBox.width / 2 - this.joystickBox.width * 0.155
        )
      } else if (this.isScaling) {
        const scalePositionX = mapRange(
          this.scale,
          MIN_SCALE,
          MAX_SCALE,
          this.joystickBox.left + this.handleHalfWidth,
          this.joystickBox.right - this.handleHalfWidth
        )

        handlePosition = {
          x: scalePositionX - this.joystickBox.left - this.joystickBox.width / 2,
          y: -2,
        }
      } else {
        handlePosition = {
          x: mapRange(
            this.position.x,
            0,
            GAME_WIDTH,
            -this.joystickBox.width / 2,
            this.joystickBox.width / 2
          ),
          y: mapRange(
            this.position.y,
            0,
            GAME_HEIGHT,
            -this.joystickBox.height / 2,
            this.joystickBox.height / 2
          ),
        }
      }

      return {
        '--handle-position-x': `${handlePosition.x}px`,
        '--handle-position-y': `${handlePosition.y}px`,
      }
    },
  },
  mounted() {
    this.handle.addEventListener('pointerdown', this.onPointerDown)
    addEventListener('pointermove', this.onPointerMove)
    addEventListener('pointerup', this.onPointerUp)
    this.$root.$on('resize', this.onResize)

    this.initialRotation = this.rotation
    this.initialScale = this.scale
    this.initialPosition = this.position
    rafRotation = this.rotation
    rafScale = this.scale
    rafPosition = this.position

    this.onResize()
    RAFHandle = requestAnimationFrame(this.onRafTick)
  },
  destroyed() {
    this.handle.removeEventListener('pointerdown', this.onPointerDown)
    removeEventListener('pointermove', this.onPointerMove)
    removeEventListener('pointerup', this.onPointerUp)
    this.$root.$off('resize', this.onResize)
  },
  methods: {
    ...mapper('pin', ['saveUndoState']),
    ...mapper(['setMoving', 'setRotating', 'setScaling']),
    setStoreRotation(angle: number) {
      this.$typedStore.pin.setRotation(angle)
    },
    setStoreScale(scale: number) {
      this.$typedStore.pin.setScale(scale)
    },
    setStorePosition(pos: { x: number; y: number }) {
      this.$typedStore.pin.setPosition(pos)
    },
    setRafRotation(angle: number) {
      rafRotation = angle
    },
    setRafScale(scale: number) {
      rafScale = scale
    },
    setRafPosition(pos: { x: number; y: number }) {
      rafPosition = pos
    },
    onResize() {
      const bb = this.joystickWrapper.getBoundingClientRect()
      const bbWidth = this.isScaling ? bb.width : bb.width * 0.8
      const square = Math.min(bbWidth, bb.height)

      this.joystick.style.width = `${square}px`
      this.joystick.style.height = `${square}px`

      this.$nextTick(() => {
        this.joystickBox = this.joystick.getBoundingClientRect()
        this.joystickCenter = {
          x: this.joystickBox.x + this.joystickBox.width / 2,
          y: this.joystickBox.y + this.joystickBox.height / 2,
        }
        this.handleHalfWidth = Math.round(this.handle.getBoundingClientRect().width * 0.4)

        // Buttons alignment relatively to joystick size and position
        if (!this.isScaling) {
          const buttonBox = this.closeButton.getBoundingClientRect()
          const radius = (square * 0.5) + (buttonBox.width * 0.5) + (square * 0.07)

          const BUTTONS_CENTER_ANGLE = -35
          const BUTTONS_ANGLE_SPACING = 14

          const angleSpacing = BUTTONS_ANGLE_SPACING + (20 * (1 - (square / 360)))

          const centerX = (this.joystickBox.width * 0.5) - (buttonBox.width * 0.5)
          const centerY = (this.joystickBox.height * 0.5) - (buttonBox.width * 0.5)
          const closeButtonX = centerX + (Math.cos((BUTTONS_CENTER_ANGLE - angleSpacing * 0.5) * (Math.PI / 180)) * radius)
          const closeButtonY = centerY + (Math.sin((BUTTONS_CENTER_ANGLE - angleSpacing * 0.5) * (Math.PI / 180)) * radius)
          const acceptButtonX = centerX + (Math.cos((BUTTONS_CENTER_ANGLE + angleSpacing * 0.5) * (Math.PI / 180)) * radius)
          const acceptButtonY = centerY + (Math.sin((BUTTONS_CENTER_ANGLE + angleSpacing * 0.5) * (Math.PI / 180)) * radius)

          this.closeButton.style.top = `${closeButtonY}px`
          this.closeButton.style.left = `${closeButtonX}px`

          this.acceptButton.style.top = `${acceptButtonY}px`
          this.acceptButton.style.left = `${acceptButtonX}px`
        } else {
          const buttonBox = this.closeButton.getBoundingClientRect()

          const BUTTONS_SPACING_X = 10
          const BUTTONS_SPACING_Y = 5
          const BUTTONS_OFFSET_X = -10
          const BUTTONS_OFFSET_Y = -(20 + (this.joystickBox.height * 0.1))
          const buttonsGroupCenterX = this.joystickBox.width - (((buttonBox.width * 2) + BUTTONS_SPACING_X) * 0.5) - (buttonBox.width * 0.5) + BUTTONS_OFFSET_X
          const buttonsGroupCenterY = (this.joystickBox.height * 0.5) - (((buttonBox.width * 2) + BUTTONS_SPACING_Y) * 0.5) - (buttonBox.width * 0.5) + BUTTONS_OFFSET_Y

          const closeButtonX = buttonsGroupCenterX
          const closeButtonY = buttonsGroupCenterY - ((buttonBox.width * 0.5) + (BUTTONS_SPACING_Y * 0.5))
          const acceptButtonX = buttonsGroupCenterX + ((buttonBox.width * 0.5) + (BUTTONS_SPACING_X * 0.5))
          const acceptButtonY = buttonsGroupCenterY + ((buttonBox.width * 0.5) + (BUTTONS_SPACING_Y * 0.5))
          const resetButtonX = buttonsGroupCenterX - ((buttonBox.width * 0.5) + (BUTTONS_SPACING_X * 0.5))
          const resetButtonY = buttonsGroupCenterY + ((buttonBox.width * 0.5) + (BUTTONS_SPACING_Y * 0.5))

          this.closeButton.style.top = `${closeButtonY}px`
          this.closeButton.style.left = `${closeButtonX}px`

          this.acceptButton.style.top = `${acceptButtonY}px`
          this.acceptButton.style.left = `${acceptButtonX}px`

          this.resetButton.style.top = `${resetButtonY}px`
          this.resetButton.style.left = `${resetButtonX}px`
        }
      })
    },
    onPointerDown: function (event: PointerEvent) {
      event.preventDefault()
      this.pointerDown = true

      if (this.isRotation) {
        this.calculateRotation(event)
      } else if (this.isScaling) {
        this.calculateScale(event)
      } else {
        this.calculatePosition(event)
      }
    },
    onPointerMove(event: PointerEvent) {
      if (!this.pointerDown) return
      event.preventDefault()

      if (this.isRotation) {
        this.calculateRotation(event)
      } else if (this.isScaling) {
        this.calculateScale(event)
      } else {
        this.calculatePosition(event)
      }
    },
    onPointerUp(event: PointerEvent) {
      if (!this.pointerDown) return

      event.preventDefault()
      this.pointerDown = false

      if (this.isRotation) {
        this.calculateRotation(event)
      } else if (this.isScaling) {
        this.calculateScale(event)
      } else {
        this.calculatePosition(event)
      }
    },
    calculateRotation(event: PointerEvent) {
      const angle = getAngle(
        event.clientX,
        event.clientY,
        this.joystickCenter.x,
        this.joystickCenter.y
      )
      this.setRafRotation(angle)
    },
    calculateScale(event: PointerEvent) {
      let { clientX } = event

      const scale = Math.min(
        Math.max(
          mapRange(
            clientX,
            this.joystickBox.left + this.handleHalfWidth,
            this.joystickBox.right - this.handleHalfWidth,
            MIN_SCALE,
            MAX_SCALE
          ),
          MIN_SCALE
        ),
        MAX_SCALE
      )

      this.setRafScale(scale)
    },
    calculatePosition(event: PointerEvent) {
      let { clientX, clientY } = event

      // When clicking outside of circle, set to closest point
      if (
        !inCircle(
          clientX,
          clientY,
          this.joystickCenter.x,
          this.joystickCenter.y,
          this.joystickBox.width / 2
        )
      ) {
        const angle = getAngle(clientX, clientY, this.joystickCenter.x, this.joystickCenter.y)
        const pos = getPositionInCircle(angle, this.joystickBox.width / 2)
        clientX = this.joystickCenter.x + pos.x
        clientY = this.joystickCenter.y + pos.y
      }

      // Set position
      this.setRafPosition({
        x: mapRange(clientX, this.joystickBox.left, this.joystickBox.right, 0, GAME_WIDTH),
        y: mapRange(clientY, this.joystickBox.top, this.joystickBox.bottom, 0, GAME_HEIGHT),
      })
    },
    onClose() {
      if (RAFHandle) {
        cancelAnimationFrame(RAFHandle)
        RAFHandle = undefined
      }

      if (this.isRotation) {
        this.setRafRotation(this.initialRotation)
        this.setStoreRotation(this.initialRotation)
        this.setRotating(false)
      } else if (this.isScaling) {
        this.setRafScale(this.initialScale)
        this.setStoreScale(this.initialScale)
        this.setScaling(false)
      } else {
        this.setRafPosition(this.initialPosition)
        this.setStorePosition(this.initialPosition)
        this.setMoving(false)
      }
    },
    onReset() {
      // Only for scaling
      this.setRafScale(DEFAULT_SCALE)
      this.setStoreScale(DEFAULT_SCALE)
    },
    onFinish() {
      if (RAFHandle) {
        cancelAnimationFrame(RAFHandle)
        RAFHandle = undefined
      }

      // Save undo state if something changed
      if (
        this.rotation !== this.initialRotation ||
        this.scale !== this.initialScale ||
        this.position.x !== this.initialPosition.x ||
        this.position.y !== this.initialPosition.y
      ) {
        const actualRotation = this.rotation
        const actualScale = this.scale
        const actualPosition = this.position
        this.setStoreRotation(this.initialRotation)
        this.setStoreScale(this.initialScale)
        this.setStorePosition(this.initialPosition)

        this.saveUndoState()

        this.setRafRotation(actualRotation)
        this.setStoreRotation(actualRotation)

        this.setRafScale(actualScale)
        this.setStoreScale(actualScale)

        this.setRafPosition(actualPosition)
        this.setStorePosition(actualPosition)
      }

      // Done
      if (this.isRotation) {
        this.setRotating(false)
      } else if (this.isScaling) {
        this.setScaling(false)
      } else {
        this.setMoving(false)
      }
    },
    onRafTick() {
      RAFHandle = requestAnimationFrame(this.onRafTick)

      if (rafRotation !== this.rotation) {
        this.setStoreRotation(rafRotation)
      }

      if (rafScale !== this.scale) {
        this.setStoreScale(rafScale)
      }

      if (rafPosition.x !== this.position.x || rafPosition.y !== this.position.y) {
        this.setStorePosition(rafPosition)
      }
    },
  },
})
